import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatStepper, MatStepperIcon, MatStep } from '@angular/material/stepper';
import { SubscriptionType } from 'src/app/types/subscription';
import { SchoolTypeEnum } from 'src/app/types/rest-api';
import { MatIcon } from '@angular/material/icon';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SchoolService } from 'src/app/services/school.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { LicenseApiService } from 'src/app/services/license-api.service';
import { ProfileService } from 'src/app/services/profile.service';
import { switchMap } from 'rxjs';
import {
  MatFormField,
  MatLabel,
  MatInput,
  MatError,
} from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SecondaryButtonComponent } from '../../../components/secondary-button/secondary-button.component';
import { OrderingConfirmationComponent } from '../components/ordering-confirmation/ordering-confirmation.component';
import { revealInvalidFields } from '../../../core/reveal-invalid-fields';

@Component({
  selector: 'app-order-process-ordering',
  templateUrl: './order-process-ordering.component.html',
  styleUrls: ['./order-process-ordering.component.scss'],
  imports: [
    MatStepper,
    MatStepperIcon,
    MatIcon,
    MatStep,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    SecondaryButtonComponent,
    OrderingConfirmationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class OrderProcessOrderingComponent implements OnInit {
  private fb = inject(FormBuilder);
  private host = inject(ElementRef);
  private router = inject(Router);
  private dialogRef = inject(MatDialogRef, { optional: true });
  private schoolService = inject(SchoolService);
  private permissionsService = inject(PermissionsService);
  private licenseApiService = inject(LicenseApiService);
  private profileService = inject(ProfileService);

  @Input() subscriptionType: SubscriptionType = 'SCHOOL_S';
  @Input() upgradeId: string | null = null;
  @Input() upgradeRole: 'schuleinheit' | 'schulgemeinde' | null = null;
  @Output() closeDialog = new EventEmitter<void>();

  @ViewChild('stepper') private stepper: MatStepper | undefined;

  schoolForm = this.fb.group({
    name: ['', [Validators.required]],
    schoolType: ['', [Validators.required]],
  });

  readonly schoolTypes = [
    {
      label: 'Öffentliche Schule',
      value: 'ÖFFENTLICHE SCHULE' as SchoolTypeEnum,
    },
    { label: 'Privatschule', value: 'PRIVATSCHULE' as SchoolTypeEnum },
    { label: 'Sonderschule', value: 'SONDERSCHULE' as SchoolTypeEnum },
  ];

  createdSchoolId: string | null = null;
  isCreating = false;
  createError = '';

  /**
   * Stable idempotency key for this dialog's order. Reused across retries of the same
   * submission so a re-click after an error replays the order instead of creating
   * (and charging) a second one; a fresh dialog gets a fresh key.
   */
  private readonly orderIdempotencyKey = crypto.randomUUID();

  ngOnInit() {
    if (this.upgradeId) {
      // Step index 1 is confirmation step in the template
      setTimeout(() => this.stepper?.next(), 0);
    }
  }

  getCurrentStep() {
    return this.stepper?.selectedIndex;
  }

  onPrev() {
    this.stepper?.previous();
  }

  onCloseDialog() {
    if (this.upgradeId) {
      this.dialogRef?.close();
      if (this.upgradeRole === 'schuleinheit') {
        this.router.navigate(['/my-schools', this.upgradeId]);
      } else {
        this.router.navigate(['/my-organization', this.upgradeId]);
      }
      window.location.reload();
      return;
    }

    if (this.createdSchoolId) {
      this.dialogRef?.close();
      this.router.navigate(['/schools', this.createdSchoolId, 'onboarding']);
    } else {
      this.closeDialog.emit();
    }
  }

  onSubmit() {
    if (!revealInvalidFields(this.schoolForm, this.host)) {
      return;
    }

    if (this.upgradeId) {
      // Profile already created in upgrade flow, just go to checkout or finish
      if (this.subscriptionType === 'SCHOOL_FREE') {
        this.onCloseDialog();
      } else {
        this.redirectToStripeCheckout();
      }
      return;
    }

    if (this.subscriptionType === 'SCHOOL_S') {
      this.redirectToStripeCheckout();
    } else {
      this.createSchool();
    }
  }

  private createSchool() {
    this.isCreating = true;
    this.createError = '';

    this.profileService.getMe(true).subscribe(profile => {
      const parts = (profile.name || '').trim().split(' ');
      const firstName = parts[0] || 'User';
      const lastName = parts.slice(1).join(' ') || 'Order';

      const billDetail: any = {
        organization_name: this.schoolForm.value.name || 'Organization',
        first_name: firstName,
        last_name: lastName,
        email: profile.email || 'noreply@bildigsnetz.ch',
        phone: (profile as any).phone_number || '-',
        street: (profile as any).street || '-',
        city: (profile as any).city || '-',
        zipcode: (profile as any).zipcode || '-',
      };

      this.licenseApiService
        .resolveOrderNumberForCreateSchool(
          billDetail,
          undefined,
          this.orderIdempotencyKey
        )
        .pipe(
          switchMap(orderNumber =>
            this.schoolService.createSchool({
              name: this.schoolForm.value.name!,
              school_type: this.schoolForm.value.schoolType! as SchoolTypeEnum,
              order_number: orderNumber,
            })
          )
        )
        .subscribe({
          next: res => {
            this.createdSchoolId = res.id;
            // Cache permission so navbar updates to school admin view
            this.permissionsService.setPermissions([
              {
                schoolId: res.id,
                userIds: [],
                users: [],
              },
            ]);
            this.isCreating = false;
            this.stepper?.next();
          },
          error: err => {
            console.error('Failed to create school:', err);
            this.createError =
              'Schule konnte nicht erstellt werden. Bitte versuche es erneut.';
            this.isCreating = false;
          },
        });
    });
  }

  private redirectToStripeCheckout() {
    this.dialogRef?.close();
    const queryParams: any = {
      plan: this.subscriptionType,
      fromOnboarding: 'true',
    };

    if (this.upgradeId) {
      queryParams.upgradeId = this.upgradeId;
      queryParams.upgradeRole = this.upgradeRole;
    } else {
      queryParams.schoolName = this.schoolForm.value.name;
      queryParams.schoolType = this.schoolForm.value.schoolType;
    }

    this.router.navigate(['/checkout/mock'], { queryParams });
  }
}
